import React, { useState } from 'react';
import Icon from 'components/AppIcon';

export default function ContributeModal({ isOpen, onClose, onContribute, goal }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const presets = [25, 50, 100, 250];
  const color = goal?.color || '#06B6D4';
  const remaining = Math.max(0, parseFloat(goal?.target_amount || 0) - parseFloat(goal?.current_amount || 0));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const val = parseFloat(amount);
    if (!val || val <= 0) { setError('Enter a valid amount'); return; }
    setSaving(true);
    setError('');
    try {
      await onContribute(goal, val, note);
      setAmount('');
      setNote('');
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to contribute');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !goal) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-[#0F172A] border border-[rgba(6,182,212,0.2)] rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-[rgba(6,182,212,0.1)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
              <Icon name={goal?.icon || 'Target'} size={18} color={color} />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-sm text-[#F8FAFC]">Quick Contribute</h3>
              <p className="text-xs text-[#64748B]">{goal?.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[rgba(6,182,212,0.1)] transition-colors">
            <Icon name="X" size={14} color="#64748B" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="bg-[#0A1628] rounded-xl p-3 text-center">
            <p className="text-xs text-[#64748B] mb-1">Remaining to goal</p>
            <p className="font-data text-xl font-bold" style={{ color }}>
              ${remaining?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          </div>

          {/* Preset amounts */}
          <div className="grid grid-cols-4 gap-2">
            {presets?.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setAmount(String(p))}
                className="py-2 rounded-lg text-xs font-medium transition-all duration-200"
                style={{
                  backgroundColor: amount === String(p) ? `${color}20` : 'rgba(15,23,42,0.8)',
                  border: `1px solid ${amount === String(p) ? color : 'rgba(6,182,212,0.1)'}`,
                  color: amount === String(p) ? color : '#64748B',
                }}
              >
                ${p}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Custom Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B] text-sm">$</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e?.target?.value)}
                placeholder="Enter amount"
                className="w-full bg-[#0A1628] border border-[rgba(6,182,212,0.15)] rounded-lg pl-7 pr-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e?.target?.value)}
              placeholder="e.g. Monthly savings"
              className="w-full bg-[#0A1628] border border-[rgba(6,182,212,0.15)] rounded-lg px-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-2.5 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.2)] rounded-lg">
              <Icon name="AlertCircle" size={13} color="#EF4444" />
              <p className="text-xs text-[#EF4444]">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[rgba(6,182,212,0.2)] text-sm text-[#94A3B8] hover:bg-[rgba(6,182,212,0.05)] transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-[#0F172A] transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(135deg, ${color}, #06B6D4)` }}
            >
              {saving ? <div className="w-4 h-4 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /> : 'Contribute'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
